// Category Trends logic shared by today's view (BranchOverview/
// CategoryTrendsView) and the redesigned tab (BranchesV2), so the KPI maths,
// the risk filters and the market tables cannot drift apart.

/* ─── Presets (Market → Category risk) ─────────────────────────────────── */
export const PRESETS = [
    { label: 'All', tip: 'Show all categories — no filter applied', yoyMin: '', yoyMax: '', qoqMin: '', qoqMax: '' },
    { label: 'YoY Declining', tip: 'Selling less than same quarter last year (YoY below -5%)', yoyMin: '', yoyMax: '-5', qoqMin: '', qoqMax: '' },
    { label: 'YoY Growing', tip: 'Selling more than same quarter last year (YoY above +5%)', yoyMin: '5', yoyMax: '', qoqMin: '', qoqMax: '' },
    { label: 'QoQ Falling', tip: 'Sales dropping vs previous quarter (QoQ below -5%)', yoyMin: '', yoyMax: '', qoqMin: '', qoqMax: '-5' },
    { label: 'QoQ Climbing', tip: 'Sales rising vs previous quarter (QoQ above +5%)', yoyMin: '', yoyMax: '', qoqMin: '5', qoqMax: '' },
    { label: 'Stable', tip: 'Both YoY and QoQ between -5% and +5% — steady demand', yoyMin: '-5', yoyMax: '5', qoqMin: '-5', qoqMax: '5' },
];

/* ─── Category → market signal mapping (heuristic, not data driven) ────── */
export const CATEGORY_SIGNALS = {
    // Row crops / grain
    Combines: ['corn', 'soybeans'],
    'Combine Platforms': ['corn', 'soybeans'],
    'Corn and Row Crop headers': ['corn', 'soybeans'],
    'Grain Carts': ['corn', 'soybeans', 'wheat'],
    'Grain Augers': ['corn', 'soybeans', 'wheat'],
    'Air Drills and Seeders': ['wheat', 'corn'],
    'Planters: Drawn': ['corn', 'soybeans'],
    'Grain Vacuums': ['corn', 'wheat'],
    // Hay / forage
    'Round Balers': ['fed_rate'],
    'Large Square Balers': ['fed_rate'],
    'Mower Conditioners': ['fed_rate'],
    Windrowers: ['wheat'],
    'Bale Grinders & Feeders': ['fed_rate'],
    // Tractors — financing + general economy
    'Row Crop Tractors': ['corn', 'fed_rate'],
    'Articulated 4WD Tractors': ['corn', 'fed_rate', 'dxy'],
    'Track Tractors': ['corn', 'fed_rate', 'dxy'],
    'Compact Utility Tractors': ['sentiment', 'fed_rate'],
    'Utility Tractors': ['sentiment', 'fed_rate'],
    // Sprayers / application
    'Self-Propelled Sprayers': ['corn', 'soybeans'],
    'Self-Propelled Applicators': ['corn', 'soybeans'],
    // Lawn & garden / consumer
    'ATVs & Gators': ['sentiment'],
    'Zero-Turn Mowers': ['sentiment'],
    'Mowers for Lawn & Garden Tractors': ['sentiment'],
    'Attachments for Lawn & Garden Tractors': ['sentiment'],
    'Commercial Front Mowers': ['sentiment'],
    // Precision ag / tech
    'Precision Ag': ['corn', 'fed_rate'],
    // Tillage
    'Vertical Tillage': ['corn', 'wheat'],
    'Chisel Plows': ['corn', 'wheat'],
    Disks: ['corn', 'wheat'],
};

/** Short labels for the driver tags (B5). */
export const DRIVER_LABELS = Object.freeze({
    corn: 'Corn', soybeans: 'Soy', wheat: 'Wheat', sentiment: 'Sentiment', fed_rate: 'Fed rate', dxy: 'USD', cli: 'CLI',
});

export const DRIVERS_TIP = 'Market signals that usually move these categories. A fixed mapping by category, not computed from the data; "—" where no mapping exists.';

/** Drivers for one or more listing category names (a sub's mf_category), merged in first-seen order. */
export const marketDrivers = (categories) => {
    const keys = [];
    (categories || []).forEach((c) => (CATEGORY_SIGNALS[c] || []).forEach((k) => {
        if (!keys.includes(k)) keys.push(k);
    }));
    return keys.map((key) => ({ key, label: DRIVER_LABELS[key] || key }));
};

/* ─── Formatting ───────────────────────────────────────────────────────── */
export function formatCurrency(value) {
    if (value == null) return '—';
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${Math.round(value / 1_000)}K`;
    return `$${value.toLocaleString('en-US')}`;
}

export const formatSignalValue = (key, v) => {
    if (v == null) return '—';
    const isCurrency = ['corn', 'soybeans', 'wheat'].includes(key);
    const isPct = key === 'fed_rate';
    return `${isCurrency ? '$' : ''}${v.toFixed(2)}${isPct ? '%' : ''}`;
};

/** Delta tone as today: bad below −5, good above +5 (inverted on request). */
export const deltaTone = (value, invert = false) => {
    if (value == null) return 'na';
    const good = invert ? value < -5 : value > 5;
    const bad = invert ? value > 5 : value < -5;
    return bad ? 'bad' : good ? 'good' : 'flat';
};

/** Backend sub-category severity → dot tone (≥4 red, ≥2 amber, else green). */
export const severityTone = (severity) => (severity >= 4 ? 'red' : severity >= 2 ? 'amber' : 'sage');

/* ─── KPI tooltips ─────────────────────────────────────────────────────── */
// D17: the backend counts used units (TYPE = 'U') for Sales TM and its YoY.
export const SALES_YOY_TIP = 'Current MTD net sales (used units only) as % of same period last year';
export const SALES_TM_TIP = 'Used net sales this month to date';
export const SALES_TM_VARIANT_TIP = 'With a variant selected: that variant\'s net sales for the current calendar month (new and used combined)';

/* ─── Quarters ─────────────────────────────────────────────────────────── */
export const quarterLabels = (trendsData) => {
    const q = trendsData?.current_quarter || '';
    const yr = trendsData?.current_year || '';
    return { q, yr, prevQ: q > 1 ? q - 1 : 4, prevQYear: q > 1 ? yr : yr - 1 };
};

export const lastCompleteYear = (trendsData) => (trendsData?.current_year || new Date().getFullYear()) - 1;

/* ─── Sales TM month ───────────────────────────────────────────────────── */
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * The month Sales TM covers. The backend takes the month to date of its own
 * "now", which it returns as generated_at (read as text, so no time zone can
 * move it). With a variant selected the figure is the client's current
 * calendar month instead (computeEffectiveKpis), so the label follows that.
 */
export const salesTmMonth = (trendsData, selectedVariant, now = new Date()) => {
    let year;
    let month;
    if (selectedVariant) {
        year = now.getFullYear();
        month = now.getMonth() + 1;
    } else {
        const m = String(trendsData?.generated_at || '').match(/^(\d{4})-(\d{2})/);
        if (!m) return null;
        year = Number(m[1]);
        month = Number(m[2]);
    }
    if (!(month >= 1 && month <= 12)) return null;
    return { short: MONTHS_SHORT[month - 1], long: `${MONTHS_LONG[month - 1]} ${year}` };
};

/* ─── Effective KPIs (variant selected) ────────────────────────────────── */
export const computeEffectiveKpis = (trendsData, selectedVariant, now = new Date()) => {
    const kpis = trendsData?.kpis ?? null;
    if (!kpis) return kpis;
    if (!selectedVariant) return kpis;
    const rollingByVariant = trendsData.rolling_avg_by_month_by_variant || {};
    const salesByVariant = trendsData.sales_by_year_month_by_variant || {};

    let avg3m = kpis.rolling_3m_avg;
    let avg6m = kpis.rolling_6m_avg;
    let avg12m = kpis.rolling_12m_avg;
    let trend = kpis.market_trend;

    const variantRolling = rollingByVariant[selectedVariant];
    if (variantRolling?.length) {
        const sorted = [...variantRolling].sort((a, b) => (b.year * 12 + b.month) - (a.year * 12 + a.month));
        const latest = sorted[0];
        avg3m = latest?.avg_3m ?? null;
        avg6m = latest?.avg_6m ?? null;
        avg12m = latest?.avg_12m ?? null;
        trend = (avg6m != null && avg12m != null)
            ? (avg6m > avg12m ? 'Uptrend' : avg6m < avg12m ? 'Downtrend' : 'Flat')
            : kpis.market_trend;
    }

    let salesTm = kpis.sales_tm;
    let salesYoy = kpis.sales_yoy_pct;
    const variantSales = salesByVariant[selectedVariant];
    if (variantSales?.length) {
        const curYear = now.getFullYear();
        const curMonth = now.getMonth() + 1;
        const curYearData = variantSales.find((s) => s.year === curYear);
        const lyData = variantSales.find((s) => s.year === curYear - 1);
        if (curYearData?.monthly_sales) {
            const tm = curYearData.monthly_sales[curMonth - 1];
            salesTm = tm != null ? tm : null;
        }
        if (curYearData?.monthly_sales && lyData?.monthly_sales) {
            const tm = curYearData.monthly_sales[curMonth - 1];
            const tmLy = lyData.monthly_sales[curMonth - 1];
            salesYoy = (tm != null && tmLy != null && tmLy > 0)
                ? Math.round((tm / tmLy) * 1000) / 10
                : null;
        }
    }

    let r3mYoy = kpis.rolling_3m_yoy;
    let r6mYoy = kpis.rolling_6m_yoy;
    let r12mYoy = kpis.rolling_12m_yoy;
    if (variantRolling?.length) {
        const byYM = {};
        variantRolling.forEach((d) => { byYM[`${d.year}-${d.month}`] = d; });
        const curYear = now.getFullYear();
        const latestCurYear = [...variantRolling].filter((d) => d.year === curYear).sort((a, b) => b.month - a.month)[0];
        if (latestCurYear) {
            const ly = byYM[`${curYear - 1}-${latestCurYear.month}`];
            if (ly) {
                const yoy = (cur, prev) => ((cur && prev) ? Math.round(((cur - prev) / prev) * 1000) / 10 : null);
                r3mYoy = yoy(latestCurYear.avg_3m, ly.avg_3m);
                r6mYoy = yoy(latestCurYear.avg_6m, ly.avg_6m);
                r12mYoy = yoy(latestCurYear.avg_12m, ly.avg_12m);
            }
        }
    }

    return {
        ...kpis,
        rolling_3m_avg: avg3m,
        rolling_6m_avg: avg6m,
        rolling_12m_avg: avg12m,
        market_trend: trend,
        sales_tm: salesTm,
        sales_yoy_pct: salesYoy,
        rolling_3m_yoy: r3mYoy,
        rolling_6m_yoy: r6mYoy,
        rolling_12m_yoy: r12mYoy,
    };
};

/* ─── Trends ───────────────────────────────────────────────────────────── */

/** Variant list Total row: Downtrend when more than half are, else Uptrend. */
export const totalTrend = (variants) => {
    if (!variants || !variants.length) return null;
    const downCount = variants.filter((v) => v.market_trend === 'Downtrend').length;
    return downCount > variants.length / 2 ? 'Downtrend' : 'Uptrend';
};

const lastNonNull = (arr) => {
    for (let i = arr.length - 1; i >= 0; i -= 1) if (arr[i] != null) return i;
    return -1;
};

/** Rolling chart subtitle: last 3M point vs last 12M point (null without data). */
export const rollingTrend = (data) => {
    const sorted = (data || []).slice().sort((a, b) => a.month - b.month);
    const d3 = sorted.map((d) => d.avg_3m);
    const d12 = sorted.map((d) => d.avg_12m);
    const i3 = lastNonNull(d3);
    const i12 = lastNonNull(d12);
    const a3 = i3 >= 0 ? d3[i3] : null;
    const a12 = i12 >= 0 ? d12[i12] : null;
    if (a3 == null && a12 == null) return null;
    return a3 != null && a12 != null && a3 > a12 ? 'Uptrend' : 'Downtrend';
};

/* ─── Category risk (Market) ───────────────────────────────────────────── */
const outOfRange = (value, min, max) => (
    (min !== '' && (value == null || value < Number(min)))
    || (max !== '' && (value == null || value > Number(max)))
);

export const subMatchesPreset = (sub, preset) => !outOfRange(sub.yoy_pct, preset.yoyMin, preset.yoyMax)
    && !outOfRange(sub.qoq_pct, preset.qoqMin, preset.qoqMax);

export const subMatchesCustom = (sub, custom) => !outOfRange(sub.yoy_pct, custom.yoyMin, custom.yoyMax)
    && !outOfRange(sub.qoq_pct, custom.qoqMin, custom.qoqMax);

/** Preset toggle: All is exclusive; removing the last one brings All back. */
export const togglePreset = (prev, idx) => {
    if (idx === 0) return new Set([0]);
    const next = new Set(prev);
    next.delete(0);
    if (next.has(idx)) {
        next.delete(idx);
        if (next.size === 0) next.add(0);
    } else {
        next.add(idx);
    }
    return next;
};

/**
 * Filter the variants at sub-category level: a sub matches the custom ranges
 * AND any active preset; a variant shows when any sub matches. `subsFor`
 * gives the subs a row shows (all of them without a filter).
 */
export const filterRiskVariants = (variants, activePresets, custom) => {
    const all = variants || [];
    const isCustom = custom.yoyMin !== '' || custom.yoyMax !== '' || custom.qoqMin !== '' || custom.qoqMax !== '';
    const isAllOnly = activePresets.size === 1 && activePresets.has(0);
    const hasFilter = !isAllOnly || isCustom;
    const activeNonAll = [...activePresets].filter((i) => i > 0);
    const subOk = (sub) => {
        if (isCustom && !subMatchesCustom(sub, custom)) return false;
        if (isAllOnly || activeNonAll.length === 0) return true;
        return activeNonAll.some((i) => subMatchesPreset(sub, PRESETS[i]));
    };
    const subsFor = (vRow) => {
        const subs = vRow.subcategories || [];
        return hasFilter ? subs.filter(subOk) : subs;
    };
    const filtered = hasFilter ? all.filter((vRow) => (vRow.subcategories || []).some(subOk)) : all;
    return { variants: filtered, hasFilter, isCustom, subsFor };
};

/** Variant row figures over its shown subs (today's exact formulas). */
export const aggregateVariantRow = (vRow, subs) => {
    const tq = subs.reduce((s, c) => s + (c.tq_sold || 0), 0);
    const lyq = subs.reduce((s, c) => s + (c.lyq_sold || 0), 0);
    const lq = subs.reduce((s, c) => s + (c.lq_sold || 0), 0);
    const critical = subs.reduce((s, c) => s + (c.critical || 0), 0);
    const daysSum = subs.reduce((s, c) => s + ((c.avg_days || 0) * (c.in_stock || 0)), 0);
    const stock = vRow.in_stock || 0;
    return {
        stock,
        avgDays: stock > 0 ? Math.round(daysSum / stock) : null,
        critical,
        tq,
        lyq,
        lq,
        yoy: vRow.yoy_pct ?? null,
        qoq: lq > 0 ? Math.round(((tq - lq) / lq) * 1000) / 10 : null,
    };
};

/** Risk KPI strip, at variant level. */
export const riskKpis = (variants) => {
    const all = variants || [];
    if (!all.length) return null;
    const withYoy = all.filter((v) => v.yoy_pct != null);
    return {
        declining: withYoy.filter((v) => v.yoy_pct < -5).length,
        growing: withYoy.filter((v) => v.yoy_pct > 5).length,
        totalUnits: all.reduce((s, v) => s + (v.in_stock || 0), 0),
        totalCritical: all.flatMap((v) => v.subcategories || []).reduce((s, c) => s + (c.critical || 0), 0),
        worst: [...withYoy].sort((a, b) => (a.yoy_pct || 0) - (b.yoy_pct || 0))[0],
        totalVariants: all.length,
    };
};
